import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Building,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  GitBranch,
  Hammer,
  Loader2,
  Network,
  Plus,
  RefreshCw,
  Search,
  Table2,
  Upload,
  Wrench,
  XCircle,
} from 'lucide-react';
import {
  SERVICE_STATUTS,
  SERVICE_TYPES,
  type ServiceStatut,
  type ServiceType,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { ServiceFormModal } from '../components/services/ServiceFormModal';
import { ServiceDetailDrawer } from '../components/services/ServiceDetailDrawer';
import { ImportServicesModal } from '../components/services/ImportServicesModal';
import { ImportAffectationsModal } from '../components/services/ImportAffectationsModal';
import {
  ServicesOrganigramme,
  type ServiceTreeNode,
} from '../components/services/ServicesOrganigramme';

/**
 * Page `/services` — vue d'ensemble du module Services (US-SVC-01 à 09).
 *
 *  - KPIs (total / actifs / équipements rattachés / équipements disponibles)
 *  - Toolbar : toggle Table / Organigramme, filtres, recherche
 *  - Vue tableau paginée + tri par colonne
 *  - Vue organigramme parent/enfant (US-SVC-03)
 *  - Click sur une ligne / un noeud → drawer détail (US-SVC-02 + 03 à 09)
 */

interface ServiceRow {
  id: string;
  code: string;
  nom_complet: string;
  type_service: ServiceType;
  statut: ServiceStatut;
  service_parent_id: string | null;
  capacite_maximale_simultanee: number | null;
  nombre_postes_travail: number | null;
  surface_totale: number | null;
  enfants_count: number;
  created_at: string;
  updated_at: string;
}

interface Pagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

interface Stats {
  total: number;
  actifs: number;
  inactifs: number;
  en_restructuration: number;
  ferme_temporairement: number;
  desaffecte: number;
  equipements_rattaches: number;
  equipements_disponibles: number;
  by_type: Array<{ type: string; count: number }>;
  by_statut: Array<{ statut: string; count: number }>;
}

type SortBy = 'code' | 'nom_complet' | 'type_service' | 'statut' | 'created_at';
type SortOrder = 'asc' | 'desc';
type ViewMode = 'table' | 'tree';

export function ServicesPage() {
  const { can } = usePermissions();
  const canRead = can('services', 'read');
  const canCreate = can('services', 'update');

  const [items, setItems] = useState<ServiceRow[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [tree, setTree] = useState<ServiceTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [treeLoading, setTreeLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [view, setView] = useState<ViewMode>('table');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statutFilter, setStatutFilter] = useState<string>('');
  const [rootOnly, setRootOnly] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>('nom_complet');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [page, setPage] = useState(1);

  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [affImportOpen, setAffImportOpen] = useState(false);
  const [affExporting, setAffExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    setErrorMessage(null);
    try {
      const res = await api.get('/services/export.xlsx', { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `services-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setExporting(false);
    }
  }

  async function handleExportAffectations() {
    setAffExporting(true);
    setErrorMessage(null);
    try {
      const res = await api.get('/services/affectations/export.xlsx', { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `affectations-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setAffExporting(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, typeFilter, statutFilter, rootOnly, sortBy, sortOrder]);

  useEffect(() => {
    if (view === 'table') refresh();
    else loadTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, debouncedSearch, typeFilter, statutFilter, rootOnly, sortBy, sortOrder, page]);

  useEffect(() => {
    loadStats();
  }, []);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (typeFilter) params.set('type_service', typeFilter);
      if (statutFilter) params.set('statut', statutFilter);
      if (rootOnly) params.set('root_only', 'true');
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
      params.set('page', String(page));
      params.set('page_size', '50');
      const res = await api.get<{ data: ServiceRow[]; pagination: Pagination }>(
        `/services?${params.toString()}`,
      );
      setItems(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function loadTree() {
    setTreeLoading(true);
    setErrorMessage(null);
    try {
      const res = await api.get<{ data: ServiceTreeNode[] }>('/services/tree');
      setTree(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setTreeLoading(false);
    }
  }

  async function loadStats() {
    try {
      const res = await api.get<{ data: Stats }>('/services/stats');
      setStats(res.data.data);
    } catch {
      // Silencieux : les KPI restent absents.
    }
  }

  function toggleSort(col: SortBy) {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortOrder('asc');
    }
  }

  function refreshAll() {
    if (view === 'table') refresh();
    else loadTree();
    loadStats();
  }

  // Map id → row pour résoudre le parent dans la colonne "Parent".
  const itemsById = useMemo(() => {
    const m = new Map<string, ServiceRow>();
    for (const it of items) m.set(it.id, it);
    return m;
  }, [items]);

  const totalLabel = pagination
    ? `${pagination.total} service(s) au total`
    : `${items.length} service(s) chargé(s)`;

  if (!canRead) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <p className="text-sm text-gray-500">Vous n'avez pas accès au module Services.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Building className="w-7 h-7 mr-2 text-primary-600" />
            Services
          </h1>
          <p className="text-sm text-gray-500 mt-1">{totalLabel}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={refreshAll}
            className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            title="Rafraîchir"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
            title="Exporter en Excel"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-1.5" />
            )}
            Exporter
          </button>
          {canCreate && (
            <button
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50"
              title="Importer depuis Excel"
            >
              <Upload className="w-4 h-4 mr-1.5" />
              Importer
            </button>
          )}
          <button
            onClick={handleExportAffectations}
            disabled={affExporting}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
            title="Exporter les affectations (responsables + rattachements)"
          >
            {affExporting ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-1.5" />
            )}
            Export affectations
          </button>
          {canCreate && (
            <button
              onClick={() => setAffImportOpen(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50"
              title="Importer des affectations (personnel ↔ services) en masse"
            >
              <Upload className="w-4 h-4 mr-1.5" />
              Import affectations
            </button>
          )}
          {canCreate && (
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau service
            </button>
          )}
        </div>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Total services" value={stats.total} icon={Building} tone="primary" />
          <KpiCard label="Services actifs" value={stats.actifs} icon={CheckCircle} tone="green" />
          <KpiCard
            label="Équipements rattachés"
            value={stats.equipements_rattaches}
            icon={Wrench}
            tone="default"
          />
          <KpiCard
            label="Équipements disponibles"
            value={stats.equipements_disponibles}
            icon={Wrench}
            tone="green"
          />
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center space-x-2">
          <ViewToggle view={view} onChange={setView} />
        </div>
        <div className="flex items-center space-x-2 flex-wrap gap-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Tous les types</option>
            {SERVICE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          <select
            value={statutFilter}
            onChange={(e) => setStatutFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Tous les statuts</option>
            {SERVICE_STATUTS.map((s) => (
              <option key={s} value={s}>
                {s.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          {view === 'table' && (
            <label className="inline-flex items-center space-x-1.5 text-sm text-gray-700 px-3 py-1.5 border border-gray-300 rounded-lg bg-white cursor-pointer">
              <input
                type="checkbox"
                checked={rootOnly}
                onChange={(e) => setRootOnly(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>Racines uniquement</span>
            </label>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-52 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {view === 'table' ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortHeader
                    label="Code"
                    col="code"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onClick={toggleSort}
                  />
                  <SortHeader
                    label="Nom complet"
                    col="nom_complet"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onClick={toggleSort}
                  />
                  <SortHeader
                    label="Type"
                    col="type_service"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onClick={toggleSort}
                    className="hidden md:table-cell"
                  />
                  <SortHeader
                    label="Statut"
                    col="statut"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onClick={toggleSort}
                  />
                  <th className="hidden lg:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Parent
                  </th>
                  <th className="hidden lg:table-cell px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Enfants
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">
                      Aucun service. Créez le premier pour démarrer.
                    </td>
                  </tr>
                ) : (
                  items.map((s) => (
                    <ServiceRowItem
                      key={s.id}
                      row={s}
                      parentLabel={
                        s.service_parent_id
                          ? (itemsById.get(s.service_parent_id)?.nom_complet ?? '—')
                          : '—'
                      }
                      onClick={() => setDetailId(s.id)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total_pages > 1 && (
            <div className="flex items-center justify-between p-3 border-t border-gray-200 text-sm">
              <span className="text-gray-500">
                Page {pagination.page} sur {pagination.total_pages}
              </span>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page === 1}
                  className="p-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                  disabled={pagination.page === pagination.total_pages}
                  className="p-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          {treeLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
            </div>
          ) : (
            <ServicesOrganigramme tree={tree} onNodeClick={(id) => setDetailId(id)} />
          )}
        </div>
      )}

      {createOpen && (
        <ServiceFormModal
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            refreshAll();
          }}
        />
      )}

      {detailId && (
        <ServiceDetailDrawer
          serviceId={detailId}
          onClose={() => setDetailId(null)}
          onRefresh={refreshAll}
        />
      )}

      {importOpen && (
        <ImportServicesModal
          onClose={() => setImportOpen(false)}
          onCompleted={() => {
            setImportOpen(false);
            refreshAll();
          }}
        />
      )}

      {affImportOpen && (
        <ImportAffectationsModal
          onClose={() => setAffImportOpen(false)}
          onCompleted={() => {
            setAffImportOpen(false);
            refreshAll();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ViewToggle({ view, onChange }: { view: ViewMode; onChange(v: ViewMode): void }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
      <button
        onClick={() => onChange('table')}
        className={`px-3 py-1.5 text-xs font-medium inline-flex items-center ${
          view === 'table'
            ? 'bg-primary-50 text-primary-700'
            : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
      >
        <Table2 className="w-3.5 h-3.5 mr-1" />
        Table
      </button>
      <button
        onClick={() => onChange('tree')}
        className={`px-3 py-1.5 text-xs font-medium inline-flex items-center border-l border-gray-300 ${
          view === 'tree'
            ? 'bg-primary-50 text-primary-700'
            : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
      >
        <Network className="w-3.5 h-3.5 mr-1" />
        Organigramme
      </button>
    </div>
  );
}

function SortHeader({
  label,
  col,
  sortBy,
  sortOrder,
  onClick,
  className,
}: {
  label: string;
  col: SortBy;
  sortBy: SortBy;
  sortOrder: SortOrder;
  onClick(col: SortBy): void;
  className?: string;
}) {
  const active = sortBy === col;
  return (
    <th
      className={`px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none ${className ?? ''}`}
      onClick={() => onClick(col)}
    >
      <span className="inline-flex items-center">
        {label}
        {active ? (
          sortOrder === 'asc' ? (
            <ArrowUp className="w-3 h-3 ml-1" />
          ) : (
            <ArrowDown className="w-3 h-3 ml-1" />
          )
        ) : null}
      </span>
    </th>
  );
}

function ServiceRowItem({
  row,
  parentLabel,
  onClick,
}: {
  row: ServiceRow;
  parentLabel: string;
  onClick(): void;
}) {
  return (
    <tr onClick={onClick} className="hover:bg-gray-50 cursor-pointer">
      <td className="px-3 py-2.5 text-xs font-semibold text-primary-600 uppercase tracking-wide whitespace-nowrap">
        {row.code}
      </td>
      <td className="px-3 py-2.5 text-gray-900">{row.nom_complet}</td>
      <td className="hidden md:table-cell px-3 py-2.5 text-gray-600 capitalize">
        {row.type_service.replace(/_/g, ' ')}
      </td>
      <td className="px-3 py-2.5">
        <ServiceStatutBadge statut={row.statut} />
      </td>
      <td className="hidden lg:table-cell px-3 py-2.5 text-gray-600 truncate max-w-[180px]">
        {parentLabel}
      </td>
      <td className="hidden lg:table-cell px-3 py-2.5 text-right text-gray-700">
        {row.enfants_count > 0 ? (
          <span className="inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border bg-blue-50 text-blue-700 border-blue-200">
            <GitBranch className="w-3 h-3 mr-1" />
            {row.enfants_count}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="px-3 py-2.5 text-right text-gray-400">
        <ChevronRight className="w-4 h-4 inline" />
      </td>
    </tr>
  );
}

function ServiceStatutBadge({ statut }: { statut: ServiceStatut }) {
  const cfg: Record<ServiceStatut, { cls: string; label: string; Icon: typeof CheckCircle }> = {
    actif: {
      cls: 'bg-green-100 text-green-800 border-green-200',
      label: 'Actif',
      Icon: CheckCircle,
    },
    inactif: {
      cls: 'bg-gray-100 text-gray-700 border-gray-200',
      label: 'Inactif',
      Icon: XCircle,
    },
    en_restructuration: {
      cls: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      label: 'En restructuration',
      Icon: Hammer,
    },
    ferme_temporairement: {
      cls: 'bg-orange-100 text-orange-800 border-orange-200',
      label: 'Fermé',
      Icon: XCircle,
    },
    desaffecte: {
      cls: 'bg-red-100 text-red-800 border-red-200',
      label: 'Désaffecté',
      Icon: XCircle,
    },
  };
  const c = cfg[statut];
  return (
    <span
      className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium border ${c.cls}`}
    >
      <c.Icon className="w-3 h-3 mr-1" />
      {c.label}
    </span>
  );
}

function KpiCard({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: number;
  icon: typeof Building;
  tone: 'default' | 'primary' | 'green';
}) {
  const tones: Record<typeof tone, string> = {
    default: 'text-gray-700',
    primary: 'text-primary-600',
    green: 'text-green-600',
  };
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <Icon className={`w-4 h-4 ${tones[tone]}`} />
      </div>
      <p className={`text-2xl font-bold mt-1 ${tones[tone]}`}>{value}</p>
    </div>
  );
}
